import { Heading, Stack, Text } from '@chakra-ui/react';
import { accountTypeOptions, markupConfigurationTypeOptions, truckingProviderOptions } from '@pelicargo/utils';
import { MarkupConfigurationType } from '@prisma/client';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { trpc } from '../../config/trpc';
import { useAuth } from '../../context/AuthProvider';
import { AirlineInput } from '../Autocomplete/AirlineInput';
import { AirportInput } from '../Autocomplete/AirportInput';
import { SimpleCheckbox } from '../SimpleCheckbox';
import { SimpleInput } from '../SimpleInput';
import { SimpleReactSelect } from '../SimpleReactSelect';
import { SimpleSelect } from '../SimpleSelect';
import { MarkupsFieldArray } from './MarkupsFieldArray';

// The server derives `is_allowed_all_*` from whether these lists are empty, which is
// the opposite of what most people expect from a filter. Say so on every field.
const ALLOW_ALL_HELPER = 'Leave empty to allow all';

type Props = {
  /**
   * When rendered from an account, the fulfillment partner is fixed to that account
   * and the picker is shown read-only.
   */
  lockedFulfillmentPartnerId?: number;
};

export const MarkupConfigurationForm = ({ lockedFulfillmentPartnerId }: Props) => {
  const { knownTenants } = useAuth();
  const { setValue } = useFormContext();
  const { data: allAccounts } = trpc.getAllAccounts.useQuery(undefined, { refetchOnWindowFocus: false });

  const accountOptions = allAccounts?.map((account) => ({ label: account?.name, value: String(account?.id) }));

  const type = useWatch({ name: 'type' });
  // Only a FULFILLMENT_PARTNER configuration is owned by an account. Leaving a partner on a
  // System/Tenant configuration makes it match as both, so clear it rather than validate it.
  const isPartnerConfiguration = type === MarkupConfigurationType.FULFILLMENT_PARTNER;

  useEffect(() => {
    if (!isPartnerConfiguration) setValue('account_as_fulfillment_partner_id', null);
  }, [isPartnerConfiguration, setValue]);

  return (
    <Stack spacing="4">
      <Stack borderWidth={1} borderRadius="md" p={4}>
        <Heading size="md">General</Heading>
        <SimpleInput name="label" label="Label" placeholder="Exchange" />
        <Stack direction="row">
          <SimpleSelect
            name="type"
            label="Type"
            options={markupConfigurationTypeOptions}
            // Creating from an account only makes sense as that account's own configuration
            isDisabled={!!lockedFulfillmentPartnerId}
          />
          <SimpleSelect
            label="Tenant"
            name="tenant_id"
            options={knownTenants.map((tenant) => ({ label: tenant, value: tenant }))}
          />
        </Stack>
      </Stack>
      <Stack borderWidth={1} borderRadius="md" p={4}>
        <Stack mb="4">
          <Heading size="md">FP-Specific</Heading>
          <Text fontSize="sm" opacity="0.7">
            This is the fulfillment partner configuration for this markup.
          </Text>
        </Stack>
        <SimpleReactSelect
          name="account_as_fulfillment_partner_id"
          label="Fulfillment Partner"
          helper={
            lockedFulfillmentPartnerId
              ? 'Fixed to this account. Edit from Markup Configurations to change it.'
              : isPartnerConfiguration
                ? 'Required for Fulfillment Partner configurations'
                : 'Not used by System / Tenant configurations'
          }
          options={accountOptions}
          isDisabled={!!lockedFulfillmentPartnerId || !isPartnerConfiguration}
          isSearchable
          isClearable
        />
        <SimpleReactSelect
          name="trucking_providers"
          label="Trucking Providers"
          helper={ALLOW_ALL_HELPER}
          options={truckingProviderOptions}
          isDisabled={!isPartnerConfiguration}
          isMulti
        />
        <SimpleCheckbox name="is_restricted" label="Restricted" isDisabled={!isPartnerConfiguration} />
      </Stack>
      <Stack borderWidth={1} borderRadius="md" p={4}>
        <Stack mb="4">
          <Heading size="md">Conditions</Heading>
          <Text fontSize="sm" opacity="0.7">
            These are the conditions that determine when this markup configuration is applied.
          </Text>
        </Stack>
        <AirportInput
          name="origin_airports"
          label="Origin Airports"
          placeholder="LAX, JFK, ORD"
          helper={ALLOW_ALL_HELPER}
          isMulti
        />
        <AirlineInput name="providers" label="Airlines" placeholder="AC, UA, EK" helper={ALLOW_ALL_HELPER} isMulti />
        <SimpleReactSelect
          name="account_as_requestors"
          label="Connected Accounts"
          helper={ALLOW_ALL_HELPER}
          options={accountOptions}
          isSearchable
          isMulti
        />
        <SimpleReactSelect
          name="account_types"
          label="Account Types"
          helper={ALLOW_ALL_HELPER}
          options={accountTypeOptions}
          isMulti
        />
      </Stack>

      <Heading size="sm">Markups</Heading>
      <MarkupsFieldArray />
    </Stack>
  );
};
